"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  RESULT_CARD,
  TABLE,
  TD,
  TEXTAREA,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  RevealedText,
  type RevealedTextStrings,
} from "@/components/tools/text/revealed-text";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { formatCodePoint } from "@/lib/tool-kit/text/code-points";
import {
  CLEANUP_CHOICES,
  type CleanupChoice,
  cleanHiddenCharacters,
  DEFAULT_CLEANUP,
  displaySegments,
  findHiddenCharacters,
  groupFindings,
  type HiddenCategory,
  type HiddenCharacterRow,
  type HiddenFinding,
  hiddenTagText,
} from "@/lib/tool-kit/text/hidden-characters";

/** Where the marked-up view stops; the table and the cleanup cover everything. */
const REVEAL_LIMIT = 20_000;
const TAG_RANGE = "U+E0000–U+E007F";
/** ASCII text spelled in invisible tag characters, for the sample. */
function toTagCharacters(text: string): string {
  return [...text]
    .map((character) =>
      String.fromCodePoint(0xe_00_00 + character.charCodeAt(0))
    )
    .join("");
}

/** English strings. A translated content file overrides these through `ui`. */
export const HIDDEN_CHARACTER_DETECTOR_STRINGS = {
  inputLabel: "Text to check",
  inputPlaceholder:
    "Paste text copied from a PDF, a web page, a chat app, an AI assistant or a code review",
  trySample: "Try a sample",
  clear: "Clear",
  sample: `﻿Meeting notes: due Friday 5 pm 👩‍💻❤️\nPlease re­view the draft​ before sending.\nStatus: ‮desrever‬\nSign in at exаmple.com\nThanks!${toTagCharacters("hidden message")}`,
  emptyPrompt: "Paste some text above to check it for hidden characters.",
  results: "Results",
  statHidden: "Hidden or look-alike characters",
  statExpected: "Doing a job, kept by default",
  statChecked: "Characters checked",
  nothingFound:
    "No hidden or look-alike characters found — only ordinary visible characters, spaces, tabs and line breaks.",
  tagMessage: "Hidden text spelled in tag characters: “{text}”",
  bidiWarning:
    "This text contains bidirectional controls, which can make it display in a different order from the order it is stored in. The view below shows the stored order.",
  revealedHeading: "Your text with hidden characters shown",
  revealedLabel:
    "Your text, with each hidden character replaced by a labeled marker",
  revealedHint:
    "Amber markers are hidden characters; gray markers are doing a job, such as joining an emoji; letters underlined in red imitate a letter from another alphabet. Hover over a marker for its name.",
  revealedTruncated:
    "Showing the first {count} characters. The table and the cleanup cover the whole text.",
  lookalikeTitle: "{name} — looks like {target}",
  tagRunTitle: "Tag characters spelling “{text}”",
  tableHeading: "Characters found",
  colShownAs: "Shown as",
  colCodePoint: "Code point",
  colName: "Name",
  colType: "Type",
  colCount: "Count",
  countWithExpected: "{count} ({expected} doing a job)",
  tagRowName: "Tag characters (a hidden copy of ASCII text)",
  categories: {
    zeroWidth: "Zero-width character",
    softHyphen: "Soft hyphen",
    blank: "Blank filler",
    specialSpace: "Special space",
    lineBreak: "Unusual line break",
    bidi: "Bidirectional control",
    tag: "Tag character",
    variationSelector: "Variation selector",
    control: "Control character",
    otherFormat: "Invisible format character",
    replacement: "Replacement or broken character",
    privateUse: "Private-use character",
    lookalike: "Look-alike letter",
  } as Record<HiddenCategory, string>,
  cleanupHeading: "Clean up",
  cleanupHint:
    "Choose what to remove or replace. Characters doing a job — inside emoji and flags, or in words of scripts that need them — are kept unless you tick the last box.",
  choices: {
    zeroWidth: "Remove zero-width characters, soft hyphens and blank fillers",
    spaces:
      "Replace special spaces with ordinary spaces, and unusual line breaks with new lines",
    bidi: "Remove bidirectional controls",
    tags: "Remove tag characters",
    selectors: "Remove variation selectors",
    controls: "Remove control characters",
    lookalikes: "Replace look-alike letters with the letters they imitate",
    placeholders: "Remove replacement and private-use characters",
  } as Record<CleanupChoice, string>,
  includeExpected:
    "Also remove characters that are doing a job (emoji joiners, flag tags, Persian and Indic joiners)",
  cleanedLabel: "Cleaned text",
  removedCount:
    "one: {count} character removed|other: {count} characters removed",
  replacedCount:
    "one: {count} character replaced|other: {count} characters replaced",
  copy: "Copy cleaned text",
  copied: "Copied!",
  download: "Download .txt",
  filename: "cleaned-text.txt",
} as const;

type Strings = typeof HIDDEN_CHARACTER_DETECTOR_STRINGS;

function rowName(row: HiddenCharacterRow, strings: Strings): string {
  if (row.category === "tag") {
    return strings.tagRowName;
  }
  return row.name ?? strings.categories[row.category];
}

function FoundTable({
  rows,
  strings,
}: {
  rows: readonly HiddenCharacterRow[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <div className="max-h-[420px] overflow-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colShownAs}
              </th>
              <th className={TH} scope="col">
                {strings.colCodePoint}
              </th>
              <th className={TH} scope="col">
                {strings.colName}
              </th>
              <th className={TH} scope="col">
                {strings.colType}
              </th>
              <th className={TH} scope="col">
                {strings.colCount}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.category}:${row.codePoint}`}>
                <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
                  {row.category === "lookalike" ? row.char : row.label}
                </td>
                <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
                  {row.category === "tag"
                    ? TAG_RANGE
                    : formatCodePoint(row.codePoint)}
                </td>
                <td className={`${TD} text-xs`}>{rowName(row, strings)}</td>
                <td className={`${TD} text-xs`}>
                  {strings.categories[row.category]}
                </td>
                <td className={`${TD} whitespace-nowrap text-xs`}>
                  {row.expectedCount > 0
                    ? t(strings.countWithExpected, {
                        count: fmt(row.count),
                        expected: fmt(row.expectedCount),
                      })
                    : fmt(row.count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cleanup({
  text,
  findings,
  strings,
}: {
  text: string;
  findings: readonly HiddenFinding[];
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [choices, setChoices] =
    useState<Record<CleanupChoice, boolean>>(DEFAULT_CLEANUP);
  const [includeExpected, setIncludeExpected] = useState(false);
  const cleaned = useMemo(
    () => cleanHiddenCharacters(text, findings, choices, includeExpected),
    [text, findings, choices, includeExpected]
  );
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.cleanupHeading}</h3>
      <p className={HINT}>{strings.cleanupHint}</p>
      <div className="space-y-2">
        {CLEANUP_CHOICES.map((choice) => (
          <Checkbox
            checked={choices[choice]}
            key={choice}
            label={strings.choices[choice]}
            onChange={(checked) =>
              setChoices((current) => ({ ...current, [choice]: checked }))
            }
          />
        ))}
        <Checkbox
          checked={includeExpected}
          label={strings.includeExpected}
          onChange={setIncludeExpected}
        />
      </div>
      <label className={LABEL} htmlFor="hidden-cleaned">
        {strings.cleanedLabel}
      </label>
      <textarea
        className={TEXTAREA}
        id="hidden-cleaned"
        readOnly
        rows={6}
        value={cleaned.text}
      />
      <p className={HINT}>
        {tPlural(strings.removedCount, locale, cleaned.removed, {
          count: fmt(cleaned.removed),
        })}
        {" · "}
        {tPlural(strings.replacedCount, locale, cleaned.replaced, {
          count: fmt(cleaned.replaced),
        })}
      </p>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={cleaned.text}
        />
        <button
          className={BUTTON_SECONDARY}
          disabled={cleaned.text.length === 0}
          onClick={() => downloadText(strings.filename, cleaned.text)}
          type="button"
        >
          {strings.download}
        </button>
      </div>
    </div>
  );
}

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const findings = useMemo(() => findHiddenCharacters(text), [text]);
  const rows = useMemo(() => groupFindings(findings), [findings]);
  const shown = text.slice(0, REVEAL_LIMIT);
  const segments = useMemo(
    () =>
      displaySegments(
        shown,
        findings.filter((f) => f.index + f.char.length <= shown.length)
      ),
    [shown, findings]
  );
  const expected = findings.filter((f) => f.expected).length;
  const suspicious = findings.length - expected;
  const tagText = hiddenTagText(findings);
  const hasBidi = findings.some((f) => f.category === "bidi" && !f.expected);
  const revealStrings: RevealedTextStrings = {
    label: strings.revealedLabel,
    lookalikeTitle: strings.lookalikeTitle,
    tagRunTitle: strings.tagRunTitle,
    categoryName: (finding) => strings.categories[finding.category],
  };
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.statHidden} value={fmt(suspicious)} />
        <StatCard label={strings.statExpected} value={fmt(expected)} />
        <StatCard label={strings.statChecked} value={fmt([...text].length)} />
      </StatGrid>
      {findings.length === 0 ? <Notice>{strings.nothingFound}</Notice> : null}
      {tagText ? (
        <p className={`${RESULT_CARD} text-foreground text-sm`}>
          {t(strings.tagMessage, { text: tagText })}
        </p>
      ) : null}
      {hasBidi ? <Notice>{strings.bidiWarning}</Notice> : null}
      {findings.length > 0 ? (
        <>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.revealedHeading}</h3>
            <p className={HINT}>{strings.revealedHint}</p>
            <RevealedText segments={segments} strings={revealStrings} />
            {text.length > REVEAL_LIMIT ? (
              <p className={HINT}>
                {t(strings.revealedTruncated, { count: fmt(REVEAL_LIMIT) })}
              </p>
            ) : null}
          </div>
          <FoundTable rows={rows} strings={strings} />
          <Cleanup findings={findings} strings={strings} text={text} />
        </>
      ) : null}
    </section>
  );
}

export default function HiddenCharacterDetectorWidget() {
  const strings = useToolUi(HIDDEN_CHARACTER_DETECTOR_STRINGS);
  const [input, setInput] = useState("");
  const text = useDeferredValue(input);
  return (
    <>
      <TextAreaField
        id="hidden-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        rows={8}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {text.length === 0 ? (
        <Notice>{strings.emptyPrompt}</Notice>
      ) : (
        <Results strings={strings} text={text} />
      )}
    </>
  );
}
